import React, { useState, useEffect } from 'react';
import "../css/chatForm.css";

const ChatForm = () => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    pronouns: '',
    badgeName: '',
    email: '',
    company: '',
    registrationType: '',
    packageType: '',
    speakerTopic: '',
  });
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [showBenefits, setShowBenefits] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const introduction = [
    { type: 'ai', text: "Hello! I'm EnergyX Africa AI. 😊" },
    { type: 'ai', text: "Welcome to the EnergyX Africa Conference registration!" },
    { type: 'ai', text: "Are you registering as a Speaker, Sponsor, or Exhibitor?" },
  ];

  const registrationOptions = [
    { type: 'speaker', prompt: "Speaker" },
    { type: 'sponsor', prompt: "Sponsor" },
    { type: 'exhibitor', prompt: "Exhibitor" },
  ];

  const speakerAndSponsorPackages = [
    { type: 'speaker', name: "Speaker Package 1", benefits: ["Speaking Slot", "Access to VIP Area", "Networking Opportunity"] },
    { type: 'speaker', name: "Speaker Package 2", benefits: ["Speaking Slot", "Promotion on Website", "Networking Opportunity"] },
    { type: 'sponsor', name: "Terawatt Sponsor : R500 000", benefits: ["Most prominent branding as Terawatt Sponsor of Energy X conference & Exhibition", "Terawatt sponsorship status exhibition space", "Opportunity to arrange interview for designated executives with our media partners" , "Participation in the conference programme" , "10x conference delegate passes" , "5x VIP passes to the opening Ceremony", "8X seats to the Energy X Conference  & Exhibition Gala Dinner"] },
    { type: 'sponsor', name: "Gigawatt Sponsor  :R400 000", benefits: ["Branding and Media Coverage", "Exclusive Booth", "Networking Dinner"] },
    { type: 'sponsor', name: "Megawatt Sponsor  :R300 000", benefits: ["Branding and Media Coverage", "Exclusive Booth", "Networking Dinner"] },
    { type: 'sponsor', name: "Kilowatt Sponsor  :R200 000", benefits: ["Branding and Media Coverage", "Exclusive Booth", "Networking Dinner"] },
    { type: 'exhibitor', name: "Exhibitor Package 1", benefits: [] },
    { type: 'exhibitor', name: "Exhibitor Package 2", benefits: [] },
  ];

  const personalDetailsQuestions = [
    { id: 'name', prompt: "What's your first name?", type: 'text' },
    { id: 'lastName', prompt: "What's your last name?", type: 'text' },
    { id: 'pronouns', prompt: "What are your pronouns?", type: 'text' },
    { id: 'badgeName', prompt: "What name would you like on your badge?", type: 'text' },
    { id: 'email', prompt: "What's your email address?", type: 'email' },
    { id: 'company', prompt: "What's your company name?", type: 'text' },
  ];

  const speakerTopics = [
    "Solar and Wind Energy Revolution",
    "Energy Storage Solutions",
    "Green Hydrogen and Ammonia Innovations",
    "Electrifying Transportation",
    "Sustainable Energy in Municipalities",
    "Workforce Development in Renewable Energy"
  ];

  useEffect(() => {
    setMessages(introduction);
  }, []);

  const handleNext = () => {
    const currentQuestions = getCurrentQuestions();
  
    if (!inputValue) {
      setError('Please fill out this field.');
      return;
    }
  
    setError('');
    setMessages([...messages, { type: 'user', text: inputValue }]);
    setFormData({ ...formData, [currentQuestions[step].id]: inputValue });
    setInputValue('');
  
    // Check if the current step is the last question in the personalDetailsQuestions
    if (step < currentQuestions.length - 1) {
      setStep(step + 1); // Increment step to go to the next question
    } else {
      setMessages([
        ...messages,
        { type: 'user', text: inputValue },
        { type: 'ai', text: `Thank you for registering, ${formData.name || 'valued participant'}!` },
      ]);
      setStep(0); // Reset step if all questions are completed
    }
  };

  const getCurrentQuestions = () => {
    if (!formData.registrationType) {
      return registrationOptions;
    }
  
    if (formData.registrationType === 'speaker' && !formData.speakerTopic) {
      return speakerTopics.map(topic => ({ prompt: topic, id: 'speakerTopic' }));
    }
  
    if ((formData.registrationType === 'sponsor' || formData.registrationType === 'exhibitor') && !formData.packageType) {
      return speakerAndSponsorPackages.filter(pkg => pkg.type === formData.registrationType);
    }
  
    // Return personal details questions, which should end with "company"
    return personalDetailsQuestions;
  };

  const handleRegistrationType = (type) => {
    setFormData({ ...formData, registrationType: type });
    setMessages([
      ...messages,
      { type: 'user', text: type },
      { type: 'ai', text: `You've selected ${type}.` },
    ]);
    setStep(0);
  };

  const handleTopicSelection = (topic) => {
    setFormData({ ...formData, speakerTopic: topic });
    setMessages([
      ...messages,
      { type: 'user', text: topic },
      { type: 'ai', text: `You've selected the topic: ${topic}. Let's proceed with your registration details.` },
    ]);
    setStep(2);
  };

  const handlePackageSelection = (pkg) => {
    setFormData({ ...formData, packageType: pkg.name });
    setSelectedPackage(pkg);
    setShowBenefits(true);
    setMessages([
      ...messages,
      { type: 'user', text: pkg.name },
      { type: 'ai', text: `You've selected ${pkg.name}. Here are the benefits:` },
    ]);
  };

  const handleContinueAfterBenefits = () => {
    setShowBenefits(false);
    setStep(1); // Proceed to personal details after viewing benefits
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  return (
    <div className="chat-form-container">
      <div className="chat-bubbles">
        {messages.map((msg, index) => (
          <div key={index} className={`bubble ${msg.type}`}>
            {msg.text}
          </div>
        ))}
      </div>

      <div className="input-container">
        {step === 0 && !formData.registrationType && (
          <div className="bubble ai">
            {registrationOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleRegistrationType(option.type)}
                className="registration-option-btn"
              >
                {option.prompt}
              </button>
            ))}
          </div>
        )}

        {formData.registrationType === 'speaker' && !formData.speakerTopic && (
          <div className="bubble ai">
            {speakerTopics.map((topic, index) => (
              <button
                key={index}
                onClick={() => handleTopicSelection(topic)}
                className="topic-option-btn"
              >
                {topic}
              </button>
            ))}
          </div>
        )}

        {(formData.registrationType === 'sponsor' || formData.registrationType === 'exhibitor') && !formData.packageType && (
          <div className="bubble ai">
            {speakerAndSponsorPackages
              .filter(pkg => pkg.type === formData.registrationType)
              .map((pkg, index) => (
                <button
                  key={index}
                  onClick={() => handlePackageSelection(pkg)}
                  className="package-option-btn"
                >
                  {pkg.name}
                </button>
              ))}
          </div>
        )}

        {showBenefits && selectedPackage && (
          <div className="bubble ai">
            <p>Benefits of {selectedPackage.name}:</p>
            <ul>
              {selectedPackage.benefits.map((benefit, index) => (
                <li key={index}>{benefit}</li>
              ))}
            </ul>
            <button onClick={handleContinueAfterBenefits} className="continue-btn">
              Continue
            </button>
          </div>
        )}

        {step !== 0 && formData.registrationType && formData.packageType && !showBenefits && (
          <>
            <div className="bubble ai">
              {personalDetailsQuestions[step - 1]?.prompt}
            </div>
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              className="input-box"
              placeholder="Your answer here..."
            />
            <button onClick={handleNext} className="next-btn">Next</button>
            {error && <p className="error">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatForm;
